import React, { memo, useState } from "react"

import useTranslation from "Pods/ThreeDStudio/Hooks/useTranslation"
import { useViewerStore } from "Pods/ThreeDStudio/Hooks/useViewerStore"
import { PROVIDER_LABELS } from "Pods/ThreeDStudio/Shared/AIEngineSelector/providerLabels"
import { useViewerContext } from "Pods/ThreeDStudio/Shared/ViewerContext/ViewerContext"
import { useThreeDStudioStore } from "Pods/ThreeDStudio/Store"
import { MATERIAL, POLYGON_TYPE } from "Pods/ThreeDStudio/Store/constants"
import {
  selectOpenedModelGenerationSettings,
} from "Pods/ThreeDStudio/Store/selectors/selectOpenedModelGenerationSettings"

import CardBody from "./Components/CardBody/CardBody"
import CompactCard from "./Components/CompactCard/CompactCard"
import { formatInteger } from "./Utils/formatInteger"

const TOPOLOGY_LABELS = {
  quad: 'Quad',
  triangle: 'Triangle',
}

const POLYGON_TYPE_LABELS = {
  [POLYGON_TYPE.TRIANGLE]: 'Triangle',
  [POLYGON_TYPE.QUADRILATERAL]: 'Quad',
}

function ModelDetailsCard() {
  const t = useTranslation()
  const viewer = useViewerContext()
  const stats = useViewerStore(viewer, "stats")
  const [isExpanded, setIsExpanded] = useState(false)

  const providerModelId = useThreeDStudioStore(state => state.ui?.openedModel?.providerModelId)
  // Derived from the immutable openedModel, not the live modelSettings / schema
  // slices the generation panel mutates. This keeps the panel fixed on the
  // settings that generated the displayed model.
  const {
    generationProvider,
    material,
    targetPolycount,
    polygonType,
    isSmartLowPoly,
  } = useThreeDStudioStore(selectOpenedModelGenerationSettings)

  if (!viewer || !stats) {
    return null
  }

  const { triangleCount, vertexCount, topologyMode } = stats

  // Settings rows only exist for opened library runs; uploaded models have no
  // generation metadata, so the card stays at the base three rows.
  const hasSettings = Boolean(providerModelId)

  const materialLabels = {
    [MATERIAL.PBR]: 'PBR',
    [MATERIAL.BASIC]: t('viewer.modelDetails.materialBasic'),
    [MATERIAL.GEOMETRY_ONLY]: t('viewer.modelDetails.materialGeometryOnly'),
  }

  const baseRows = [
    { label: t('viewer.modelDetails.topology'), value: TOPOLOGY_LABELS[topologyMode] || '—' },
    { label: t('viewer.modelDetails.faces'), value: formatInteger(triangleCount) },
    { label: t('viewer.modelDetails.vertices'), value: formatInteger(vertexCount) },
  ]

  const settingsRows = [
    { label: t('viewer.modelDetails.aiEngine'), value: PROVIDER_LABELS[generationProvider] || '—' },
    {
      label: t('viewer.modelDetails.lowPoly'),
      value: isSmartLowPoly ? t('viewer.modelDetails.true') : t('viewer.modelDetails.false'),
    },
    { label: t('viewer.modelDetails.polycount'), value: formatInteger(targetPolycount) },
    { label: t('viewer.modelDetails.polygonType'), value: POLYGON_TYPE_LABELS[polygonType] || '—' },
    { label: t('viewer.modelDetails.material'), value: materialLabels[material] || '—' },
  ]

  const rows = hasSettings && isExpanded ? [...baseRows, ...settingsRows] : baseRows

  const handleToggle = () => setIsExpanded(prev => !prev)

  const cardBody = (
    <CardBody rows={rows} hasSettings={hasSettings} isExpanded={isExpanded} onToggle={handleToggle} />
  )

  return (
    <>
      <div className="model-details-card model-details-card--inline">
        {cardBody}
      </div>
      <CompactCard>{cardBody}</CompactCard>
    </>
  )
}

export default memo(ModelDetailsCard)
